import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { FileUploadState, FileUploadAction } from '@state/file-upload.state';
import { FileInfoDto } from 'app/@types/dto/file-info-dto.model';
import { Observable, Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UrlService } from '@services/datasources/url.service';

import { ToastService } from '@services/common/toast.service';

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    standalone: false,
})
export class FileUploadComponent implements OnInit, OnDestroy, OnChanges {
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() orgId: string;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() entityId: string;
    @Input() measureRowId?: string;

    @Output() fileSelected = new EventEmitter<File[]>();
    @Output() fileUploaded = new EventEmitter<FileInfoDto[]>();
    @Output() combinedFilesChanged = new EventEmitter<FileInfoDto[]>();
    @Output() filesToDeleteChanged = new EventEmitter<string[]>();

    @Select(FileUploadState.getFiles) files$!: Observable<FileInfoDto[]>;
    @Select(FileUploadState.isLoading) loading$!: Observable<boolean>;
    @Select(FileUploadState.hasErrors) hasErrors$!: Observable<boolean>;

    selectedFiles: FileInfoDto[] = [];
    isDisabled = false;
    combinedFiles: FileInfoDto[] = [];
    filesToDelete: string[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private store: Store,
        private http: HttpClient,
        private urlService: UrlService,
        private toastService: ToastService,
    ) {
        // Subscribe to loading$ to update isDisabled
        this.loading$.pipe(takeUntil(this.destroy$)).subscribe((isLoading) => (this.isDisabled = isLoading));
    }

    ngOnInit() {
        this.loadFiles();
        this.files$.pipe(takeUntil(this.destroy$)).subscribe((files) => {
            this.fileUploaded.emit(files);
            this.updateCombinedFiles(files);
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Reload files when measureRowId, orgId, or entityId changes (but not on first initialization)
        const hasRelevantChanges = changes['measureRowId'] || changes['orgId'] || changes['entityId'];

        if (hasRelevantChanges) {
            this.selectedFiles = []; // Reset selected files on any relevant change
            this.filesToDelete = []; // Reset files to delete
            this.loadFiles();
            this.updateCombinedFiles();
        }
    }

    /** Loads the list of existing files from the backend */
    loadFiles() {
        if (!this.orgId || !this.entityId) {
            return;
        }

        // If measureRowId is null, we are in creation mode → no existing files to display
        if (!this.measureRowId) {
            this.store.dispatch(new FileUploadAction.ClearFiles());
            this.resetComponent(); // Reset the component
            return;
        }

        this.store.dispatch(new FileUploadAction.GetFiles(this.orgId, this.entityId, this.measureRowId));
    }

    /** Updates the combined files array and emits the change */
    private updateCombinedFiles(uploadedFiles?: FileInfoDto[]) {
        // Get uploaded files from the observable or use the provided parameter
        if (uploadedFiles) {
            this.combinedFiles = [...uploadedFiles, ...this.selectedFiles];
        } else {
            // If no parameter provided, get current uploaded files from the store
            this.files$.pipe(take(1)).subscribe((files) => {
                this.combinedFiles = [...files, ...this.selectedFiles];
                this.combinedFilesChanged.emit(this.combinedFiles);
            });
            return;
        }
        this.combinedFilesChanged.emit(this.combinedFiles);
    }

    /** Checks if the file is valid */
    private isValidFile(file: File): boolean {
        const maxSize = 50 * 1024 * 1024; // 50MB
        const allowedTypes = ['.pdf', '.docx', '.xlsx', '.csv', '.jpeg', '.png', '.txt'];
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();

        return file.size <= maxSize && allowedTypes.includes(extension);
    }

    /** Handles the selection of a new file */
    onFileSelected(event: Event) {
        const target = event.target as HTMLInputElement;
        const file = target?.files?.[0];

        if (file && this.isValidFile(file)) {
            const newFile: FileInfoDto = {
                id: '', // Temporary ID, will be set after upload
                organization_id: this.orgId || '',
                questionnaireRowId: this.measureRowId || '',
                organizationEntityId: this.entityId || '',
                size: file.size,
                creation_ts: Date.now(),
                name: file.name,
                confirmed: false,
                contentType: file.type,
                file: file, // Adding file property for temporary use
            } as FileInfoDto & { file: File };

            this.selectedFiles.push(newFile);
            this.fileSelected.emit(this.selectedFiles.map((f) => (f as FileInfoDto & { file: File }).file));
            this.updateCombinedFiles();
        } else if (file) {
            this.toastService.showError({
                details: 'components.file_upload.error_message',
            });
        }

        // Reset input value to allow selecting the same file again
        if (target) {
            target.value = '';
        }
    }

    /** Uploads the selected files */
    uploadSelectedFiles(measureRowId: string): void {
        if (!this.orgId || !this.entityId || this.selectedFiles.length === 0) {
            return;
        }
        // Upload all selected files
        this.selectedFiles.forEach((selectedFile) => {
            this.store.dispatch(
                new FileUploadAction.UploadFile(
                    this.orgId,
                    this.entityId,
                    (selectedFile as FileInfoDto & { file: File }).file,
                    measureRowId,
                ),
            );
        });

        // Clear selected files after initiating upload
        this.selectedFiles = [];
        this.fileSelected.emit([]);
    }

    /** Removes a selected file from the list */
    removeSelectedFile(index: number) {
        this.selectedFiles.splice(index, 1);
        this.fileSelected.emit(this.selectedFiles.map((f) => (f as FileInfoDto & { file: File }).file));
        this.updateCombinedFiles();
    }

    /** Resets the component to its initial state */
    resetComponent() {
        this.selectedFiles = [];
        this.filesToDelete = [];
        this.fileSelected.emit([]);
        this.filesToDeleteChanged.emit([]);
        this.updateCombinedFiles();

        // Reset the input file
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    /** Toggles file selection for deletion */
    toggleFileForDeletion(fileId: string) {
        const index = this.filesToDelete.indexOf(fileId);
        if (index > -1) {
            this.filesToDelete.splice(index, 1);
        } else {
            this.filesToDelete.push(fileId);
        }
        this.filesToDeleteChanged.emit(this.filesToDelete);
    }

    /** Checks if file is selected for deletion */
    isFileSelectedForDeletion(fileId: string): boolean {
        return this.filesToDelete.includes(fileId);
    }

    /** Deletes selected files - called from parent component */
    deleteSelectedFiles(): void {
        if (!this.orgId || !this.entityId || this.filesToDelete.length === 0) {
            return;
        }

        this.filesToDelete.forEach((fileId) => {
            this.store.dispatch(new FileUploadAction.DeleteFile(this.orgId, this.entityId, fileId, this.measureRowId));
        });

        // Clear the selection after deletion
        this.filesToDelete = [];
        this.filesToDeleteChanged.emit([]);
    }

    /** Downloads a file */
    onDownload(file: FileInfoDto) {
        if (!this.orgId || !this.entityId) {
            return;
        }

        let params = new HttpParams().set('fileId', file.id).set('organizationEntityId', this.entityId);

        if (this.measureRowId) {
            params = params.set('questionnaireRowId', this.measureRowId);
        }

        const url = this.urlService.getFileDownloadUrl(this.orgId);

        this.http
            .get(url, {
                params,
                responseType: 'blob',
                observe: 'response',
            })
            .subscribe({
                next: (response) => {
                    const blob = response.body;
                    if (blob) {
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = file.name;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();

                        setTimeout(() => {
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(downloadUrl);
                        }, 100);
                    }
                },
                error: (error) => {
                    console.error('Download failed:', error);
                },
            });
    }

    /** Formats the size of a file */
    formatFileSize(bytes: number): string {
        if (bytes === 0) {
            return '0 B';
        }
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}
